import { useFormik } from "formik";
import React, { FC, useEffect, useState } from "react";
import axios from "axios";
import { connect } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { CustomizedLabelInput } from "../../components/core/inputs/CustomizedLabelInput";
import { FORM_BUTTONS, INPUTS } from "../../constants/ui";
import {
  FORGOT_PASSWORD_ROUTE,
  SIGN_IN_ROUTE,
  SIGN_UP_COMPANY_ROUTE,
} from "../../store/constants/route-constants";
import { Dispatch, RootState } from "../../store/store";
import { RegisterPageWrapper } from "../RegisterPageWrapper";
import { CustomizedFormButton } from "../../components/core/buttons/CustomizedFormButton";
import { APP_NETWORK } from "../../network";

type SignInProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

const SignInPage: FC<SignInProps> = ({ isAuthenticated, signIn }) => {
  const navigate = useNavigate();
  const [otherApps, setOtherApps] = useState<
    { value: string; app_url: string }[]
  >([]);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/business-networks/apps`)
      .then(({ data }) =>
        setOtherApps(
          data.filter(
            (network: { value: string; app_url?: string }) =>
              network.app_url && network.value !== APP_NETWORK,
          ),
        ),
      )
      .catch(() =>
        setOtherApps([
          {
            app_url: "https://hedensted.baeredygtighedsmodel.dk",
            value: "Hedensted",
          },
          {
            app_url: "https://app.baeredygtighedsmodel.dk",
            value: "Billund",
          },
        ]),
      );
  }, []);

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string().email("Invalid email").required("Required"),
      password: Yup.string().required("Required"),
    }),
    onSubmit: async (values) => {
      await signIn({
        email: values.email,
        password: values.password,
      });
    },
    enableReinitialize: false,
  });

  if (isAuthenticated) {
    return <Navigate to="/" />;
  }

  return (
    <RegisterPageWrapper
      title="Log ind"
      action={{
        title: "Log ind",
        handler: formik.handleSubmit,
        disabled: !(formik.isValid && formik.dirty),
      }}
      showBackButton={false}
    >
      <div className="mb-6">
        <CustomizedLabelInput
          autoFocus
          id="email"
          autoComplete="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          error={formik.values.email && formik.errors.email}
          type="email"
          label="Email"
          placeHolder="Din mailadresse"
          styleType={
            formik.values.email && formik.errors.email
              ? INPUTS.ERROR
              : INPUTS.DEFAULT_FULL_WIDTH
          }
          disabled={false}
        />
      </div>
      <div className="mb-6">
        <CustomizedLabelInput
          id="password"
          autoComplete="new-password"
          value={formik.values.password}
          onChange={formik.handleChange}
          error={formik.values.password && formik.errors.password}
          type="password"
          label="Kodeord"
          placeHolder="Kodeord"
          styleType={
            formik.values.password && formik.errors.password
              ? INPUTS.ERROR
              : INPUTS.DEFAULT_FULL_WIDTH
          }
          disabled={false}
        />
        <div className="mt-2 text-right">
          <a
            className="cursor-pointer"
            onClick={() => navigate(FORGOT_PASSWORD_ROUTE)}
          >
            Glemt adgangskode
          </a>
        </div>
      </div>

      <div className="text-center">
        <CustomizedFormButton
          text="Opret virksomhed"
          btn_type={FORM_BUTTONS.BLUE}
          disabled={false}
          onClick={() => navigate(SIGN_UP_COMPANY_ROUTE)}
          type="button"
        />
      </div>
      {otherApps.length > 0 && (
        <div className="mt-6 text-center">
          <p>Tilhører du et andet netværk? Log ind i dit netværks app:</p>
          <div className="mt-2 flex flex-wrap justify-center gap-4">
            {otherApps.map((network) => (
              <a
                key={network.app_url}
                className="underline"
                href={`${network.app_url}${SIGN_IN_ROUTE}`}
              >
                {network.value}
              </a>
            ))}
          </div>
        </div>
      )}
    </RegisterPageWrapper>
  );
};

const mapState = (state: RootState) => ({
  isAuthenticated: state.user?.isAuthenticated,
});

const mapDispatch = (dispatch: Dispatch) => ({
  signIn: dispatch.user.signIn,
});

export default connect(mapState, mapDispatch)(SignInPage);
